<template>
  <form
    class="search-autosuggest"
    ref="form"
    @click.stop
    @submit.prevent
  >
    <input-vue
      class="search-autosuggest__input"
      ref="input"
      :placeholder="$t('searchbox.placeholder')"
      :button="{
        type: 'button',
        icon: 'search'
      }"
      :name="name"
      autocomplete="off"
      @keyUpEvent="inputKeyUpHandler"
      @focusin="inputFocusInHandler"
      @buttonclicked="submitForm"
      @resetInput="resetQuery"
      :aria-label="$t('global.search.aria.label')"
      v-model="query"
      type="search"
    />
    <input
      type="hidden"
      name="ms"
      value="true"
    >
    <div
      class="search-autosuggest__suggestions-flyout"
      v-if="suggestions.length && this.showSuggestions"
    >
      <div
        v-for="(suggestionsList, listIndex) in suggestions"
        :key="listIndex"
        class="search-autosuggest__suggestions-list"
      >
        <template
          v-if="suggestionsList.suggestions && suggestionsList.suggestions.length"
        >
          <span
            class="search-autosuggest__suggestions-name"
            v-if="suggestionsList.suggestions[0].title"
          >
            {{ suggestionsList.suggestions[0].title }}
          </span>
          <a
            class="search-autosuggest__suggestion"
            v-for="(suggestion, suggestionIndex) in suggestionsList.suggestions"
            :key="suggestionIndex"
            :class="{'search-autosuggest__suggestion--active': activeListIndex === listIndex && activeSuggestionIndex === suggestionIndex}"
            :href="suggestion.link ? suggestion.link : '#'"
            @click.prevent="chooseSuggestion(listIndex, suggestionIndex)"
          >
            {{ suggestion.value }}
          </a>
        </template>
      </div>
    </div>
  </form>
</template>

<script>
import InputVue from '../inputVue/inputVue.vue'
import searchSuggestions from '../../services/searchSuggestions/searchSuggestions.js'
import pigeon from '../../utilities/js/pigeon/pigeon.js'

export default {
  components: {
    InputVue
  },
  props: {
    initialValue: {
      type: String,
      default: ''
    },
    name: String
  },
  data() {
    return {
      example: 'searchAutosuggest',
      showSuggestions: false,
      previousQuery: '',
      pendingRequest: null,
      suggestions: [],
      activeListIndex: -1,
      activeSuggestionIndex: -1,
      query: this.initialValue
    }
  },
  mounted() {
    const body = document.querySelector('body')
    body.addEventListener('click', this.hideSuggestions.bind(this))
  },
  methods: {
    resetQuery() {
      this.query = ''
    },
    inputFocusInHandler() {
      const suggest = new Promise(resolve => {
        if (this.initialValue !== '') {
          this.search()
          resolve(this.suggestions.length)
        } else {
          resolve(0)
        }
      })
      this.showSuggestions = suggest.then(length => length > 0)
    },
    inputKeyUpHandler(event) {
      if (event.key === 'Enter' || event.code === 'Space') {
        event.preventDefault();
        this.submitKeyHandler()
      } else if (event.key === 'ArrowUp' && this.suggestions && this.suggestions.length) {
        this.prevSuggestion()
      } else if (event.key === 'ArrowDown' && this.suggestions && this.suggestions.length) {
        this.nextSuggestion()
      } else if (this.query !== '' && this.query !== this.previousQuery) {
        this.previousQuery = this.query
        this.search()
      } else if (this.query === '') {
        this.previousQuery = ''
        this.showSuggestions = false
        this.suggestions = []
        this.activeListIndex = -1
        this.activeSuggestionIndex = -1
      }
    },
    search() {
      this.pendingRequest = searchSuggestions()
        .searchForQuery(encodeURIComponent(this.query))
        .catch(error => {
          pigeon.publish('globalAppError:show', {error: error})
        })

      this.pendingRequest
        .then(this.updateSuggestions.bind(this))
    },
    updateSuggestions(suggestions) {
      this.suggestions = suggestions
      this.showSuggestions = !!this.suggestions.length
    },
    chooseSuggestion(listIndex, suggestionIndex) {
      const suggestion = this.suggestions[listIndex].suggestions[suggestionIndex]

      if (suggestion.link) {
        window.location.href = suggestion.link
      } else {
        this.query = suggestion.value
        this.$nextTick(this.submitForm.bind(this))
      }
    },
    submitForm() {
      this.$refs.form.submit()
    },
    submitKeyHandler() {
      if (this.activeListIndex > -1 && this.activeSuggestionIndex > -1) {
        this.chooseSuggestion(this.activeListIndex, this.activeSuggestionIndex)
      } else {
        this.submitForm()
      }
    },
    hideSuggestions() {
      this.showSuggestions = false
    },
    nextSuggestion() {
      this.activeListIndex = this.activeListIndex < 0 ? this.getNextListIndex() : this.activeListIndex

      if (this.activeSuggestionIndex + 1 > this.suggestions[this.activeListIndex].suggestions.length - 1) {
        this.activeListIndex = this.getNextListIndex()
        this.activeSuggestionIndex = this.activeListIndex >= 0 ? 0 : -1
      } else {
        this.activeSuggestionIndex += 1
      }
    },
    getNextListIndex() {
      let currentIndex = this.activeListIndex
      let nextIndex

      for (let i = currentIndex; i < this.suggestions.length; i++) {
        if (this.suggestions[i + 1]) {
          if (this.suggestions[i + 1].suggestions && this.suggestions[i + 1].suggestions.length) {
            nextIndex = i + 1
            break
          }
        } else {
          nextIndex = -1
          break
        }
      }

      return nextIndex
    },
    prevSuggestion() {
      if (this.activeSuggestionIndex - 1 < 0) {
        this.activeListIndex = this.getPrevListIndex()
        this.activeSuggestionIndex = this.activeListIndex >= 0 ? this.suggestions[this.activeListIndex].suggestions.length - 1 : -1
      } else {
        this.activeSuggestionIndex -= 1
      }
    },
    getPrevListIndex() {
      let currentIndex = this.activeListIndex
      let prevIndex

      for (let i = currentIndex; i >= -1; i--) {
        if (i - 1 < -1) {
          i = this.suggestions.length
        }

        if (this.suggestions[i - 1]) {
          if (this.suggestions[i - 1].suggestions && this.suggestions[i - 1].suggestions.length) {
            prevIndex = i - 1
            break
          }
        } else {
          prevIndex = -1
          break
        }
      }

      return prevIndex
    }
  }
}

</script>
